(() => {
  if (customElements.get('delivery-slot-picker')) return;

  const IST = 5.5 * 3600 * 1000;
  const pad = (n) => String(n).padStart(2, '0');
  const nowIST = () => new Date(Date.now() + IST);                    // read with getUTC* = IST wall clock
  const ymd = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const hLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 || h === 24 ? 'AM' : 'PM'}`;
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  class DeliverySlotPicker extends HTMLElement {
    connectedCallback() {
      this.cfg = {
        open: +this.dataset.open || 9, close: +this.dataset.close || 22,
        len: +this.dataset.slotHours || 2, lead: +this.dataset.leadHours || 2, days: +this.dataset.days || 7,
      };
      this.date = this.dataset.date || '';
      this.slot = this.dataset.slot || '';
      if (!this.isAvailable(this.date, this.slot)) { this.date = ''; this.slot = ''; }
      if (!this.date) { this.date = this.availableDates()[0] || ''; }
      this.render();
      DeliverySlotPicker.bindCheckoutGuard();
      // The theme re-renders the cart (morph) and wipes our buttons without reconnecting this element → draw them again
      if (!this.observer) {
        this.observer = new MutationObserver(() => {
          if (this.pending || this.querySelector('[data-role="dates"] select')) return;
          this.pending = requestAnimationFrame(() => { this.pending = 0; this.render(); });
        });
      }
      this.observer.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }

    slotsFor() {                                                       // [{start, end, label}]
      const out = [];
      for (let s = this.cfg.open; s < this.cfg.close; s += this.cfg.len) {   // e.g. 8-10, 10-12 ... 8-10 PM, last one 10-11 PM
        const end = Math.min(s + this.cfg.len, this.cfg.close);
        out.push({ start: s, label: `${hLabel(s)} - ${hLabel(end)}` });
      }
      return out;
    }

    isOpenSlot(day, start) {                                           // slot END must be >= now + lead (IST) -> order is ready within the slot
      const now = nowIST();
      const [y, m, d] = day.split('-').map(Number);
      const end = Math.min(start + this.cfg.len, this.cfg.close);
      const endMs = Date.UTC(y, m - 1, d, end, 0, 0);
      return endMs >= now.getTime() + this.cfg.lead * 3600 * 1000;
    }

    availableDates() {
      const out = []; const base = nowIST();
      for (let i = 0; i < this.cfg.days + 1 && out.length < this.cfg.days; i++) {
        const d = new Date(base.getTime() + i * 86400000); const key = ymd(d);
        if (this.slotsFor().some((s) => this.isOpenSlot(key, s.start))) out.push(key);
      }
      return out;
    }

    isAvailable(day, label) {
      if (!day || !label) return false;
      const s = this.slotsFor().find((x) => x.label === label);
      return !!s && this.availableDates().includes(day) && this.isOpenSlot(day, s.start);
    }

    dateLabel(key, i) {
      const [y, m, d] = key.split('-').map(Number); const dt = new Date(Date.UTC(y, m - 1, d));
      const today = ymd(nowIST()); const tomorrow = ymd(new Date(nowIST().getTime() + 86400000));
      const name = key === today ? 'Today' : key === tomorrow ? 'Tomorrow' : DAYS[dt.getUTCDay()];
      return `${name}, ${d} ${MONTHS[m - 1]}`;
    }

    button(text, onClick) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'delivery-slot__btn'; b.textContent = text; b.setAttribute('role', 'radio');
      // Keep the click inside the cart drawer (the drawer closes on clicks it thinks are outside)
      b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); if (!b.disabled) onClick(); });
      return b;
    }

    render() {                                                         // build buttons once (again only if the theme wiped them)
      this.datesEl = this.querySelector('[data-role="dates"]');
      this.slotsEl = this.querySelector('[data-role="slots"]');
      this.errorEl = this.querySelector('.delivery-slot__error');
      if (!this.datesEl || !this.slotsEl) return;
      const sel = document.createElement('select');                    // date = dropdown
      sel.className = 'delivery-slot__select'; sel.setAttribute('aria-label', 'Delivery date');
      sel.append(...this.availableDates().map((key, i) => new Option(this.dateLabel(key, i), key)));
      sel.addEventListener('click', (e) => e.stopPropagation());
      sel.addEventListener('change', (e) => { e.stopPropagation(); this.date = sel.value; this.slot = ''; this.save(); this.update(); });
      this.datesEl.replaceChildren(sel);
      this.slotsEl.replaceChildren(...this.slotsFor().map((s) => {
        const b = this.button(s.label, () => { this.slot = s.label; this.save(); this.update(); this.showError(false); });
        b.dataset.start = s.start; b.dataset.label = s.label; return b;
      }));
      this.update();
    }

    update() {                                                         // change selected / disabled state in place – no DOM replacement
      const sel = this.datesEl.querySelector('select'); if (sel && sel.value !== this.date) sel.value = this.date;
      this.slotsEl.querySelectorAll('button').forEach((b) => {
        b.disabled = !this.date || !this.isOpenSlot(this.date, +b.dataset.start);
        b.setAttribute('aria-checked', !b.disabled && b.dataset.label === this.slot ? 'true' : 'false');
      });
    }

    async save() {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      try {
        await fetch(`${root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { 'Delivery Date': this.slot ? this.date : '', 'Delivery Slot': this.slot } }),
        });
        this.dataset.date = this.slot ? this.date : ''; this.dataset.slot = this.slot;
      } catch (e) { /* keep UI state; checkout guard re-checks */ }
    }

    showError(on) { this.errorEl.hidden = !on; this.classList.toggle('delivery-slot--invalid', on); }

    valid() { return this.isAvailable(this.date, this.slot); }

    static bindCheckoutGuard() {
      if (DeliverySlotPicker.guarded) return; DeliverySlotPicker.guarded = true;
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[name="checkout"], #checkout, .cart__checkout-button');
        if (!btn) return;
        const pickers = [...document.querySelectorAll('delivery-slot-picker')].filter((p) => p.offsetParent !== null);
        const picker = pickers[0] || document.querySelector('delivery-slot-picker');
        if (picker && !picker.valid()) {
          e.preventDefault(); e.stopImmediatePropagation();
          picker.showError(true); picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }
  }

  customElements.define('delivery-slot-picker', DeliverySlotPicker);
})();
